import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@root/globalTypes';
import { queryKeys, uuidRegEx } from '@utils/consts';
import { queryFetch } from '@services/api';
import { JobStagesRespSuccess } from './types';
import { useParams } from 'react-router';

export const JOB_STAGES_HINT_STALE_TIME = 60 * 1000;

interface UseGetJobStagesParams {
  projectId?: string;
  jobId?: string;
  enabled?: boolean;
  staleTime?: number;
}

const canFetchJobStages = (projectId?: string, jobId?: string) =>
  Boolean(projectId && jobId && uuidRegEx.test(jobId));

const getJobStagesQuery = (projectId?: string, jobId?: string) => ({
  queryKey: [...queryKeys.jobStages, projectId, jobId],
  queryFn: (): Promise<JobStagesRespSuccess> =>
    queryFetch({ url: `projects/${projectId}/jobs/${jobId}/stages` }),
});

export const useGetJobStages = (params: UseGetJobStagesParams = {}) => {
  const routeParams = useParams();
  const projectId = params.projectId ?? routeParams.projectId;
  const jobId = params.jobId ?? routeParams.jobId;
  const enabled = params.enabled ?? true;

  const { data, error, isLoading } = useQuery<JobStagesRespSuccess, ApiError>({
    ...getJobStagesQuery(projectId, jobId),
    placeholderData: (previousData) => previousData as JobStagesRespSuccess,
    enabled: enabled && canFetchJobStages(projectId, jobId),
    staleTime: params.staleTime,
  });

  return {
    data,
    error,
    isLoading,
  };
};

export const usePrefetchJobStages = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (projectId?: string, jobId?: string) => {
      if (!canFetchJobStages(projectId, jobId)) return;
      void queryClient.prefetchQuery({
        ...getJobStagesQuery(projectId, jobId),
        staleTime: JOB_STAGES_HINT_STALE_TIME,
      });
    },
    [queryClient],
  );
};
